/**
 * Prefetches voter page URLs for currently visible map markers.
 */

import { PREFETCH_MAX, PREFETCH_DEBOUNCE_MS, isShowAllView } from './config.js';
import { map, theMarkers } from './state.js';

/** @type {Set<string>} URLs already prefetched this page load. */
const prefetchedUrls = new Set();

/** @type {number|null} Debounce timer id. */
let debounceTimer = null;

/**
 * Whether prefetching should run for the current environment.
 *
 * Skips show-all view, Save-Data, and slow connections.
 *
 * @return {boolean} True when prefetch is allowed.
 */
function shouldPrefetch() {
	if ( isShowAllView() ) {
		return false;
	}

	const connection = navigator.connection;
	if ( connection?.saveData ) {
		return false;
	}

	const effectiveType = connection?.effectiveType;
	if ( '2g' === effectiveType || 'slow-2g' === effectiveType ) {
		return false;
	}

	return true;
}

/**
 * Whether a URL is same-origin and safe to prefetch.
 *
 * @param {string} url Marker permalink.
 * @return {boolean} True for same-origin http(s) URLs.
 */
function isSameOriginUrl( url ) {
	try {
		const parsed = new URL( url, window.location.origin );
		return (
			parsed.origin === window.location.origin &&
			( 'http:' === parsed.protocol || 'https:' === parsed.protocol )
		);
	} catch ( error ) {
		return false;
	}
}

/**
 * Collects permalinks for markers currently shown on the map.
 *
 * @return {string[]} Visible marker URLs.
 */
function collectVisibleMarkerUrls() {
	const urls = [];

	for ( const status of Object.keys( theMarkers ) ) {
		const group = theMarkers[ status ];
		for ( let i = 0; i < group.length; i++ ) {
			const marker = group[ i ];
			if ( marker.map === map && marker.url ) {
				urls.push( marker.url );
			}
		}
	}

	return urls;
}

/**
 * Injects a prefetch link for a voter URL if not already requested.
 *
 * @param {string} url Voter permalink.
 * @return {void}
 */
function injectPrefetchLink( url ) {
	if ( prefetchedUrls.has( url ) ) {
		return;
	}

	prefetchedUrls.add( url );

	const link = document.createElement( 'link' );
	link.rel = 'prefetch';
	link.href = url;
	link.as = 'document';
	document.head.appendChild( link );
}

/**
 * Schedules prefetch of visible marker URLs after visibility changes.
 *
 * @return {void}
 */
export function prefetchVisibleMarkerUrls() {
	if ( ! map || ! shouldPrefetch() ) {
		return;
	}

	if ( debounceTimer ) {
		window.clearTimeout( debounceTimer );
	}

	debounceTimer = window.setTimeout( () => {
		debounceTimer = null;

		const urls = collectVisibleMarkerUrls()
			.filter( isSameOriginUrl )
			.slice( 0, PREFETCH_MAX );

		for ( let i = 0; i < urls.length; i++ ) {
			injectPrefetchLink( urls[ i ] );
		}
	}, PREFETCH_DEBOUNCE_MS );
}
